<script setup lang="ts">
import {
  Autoplay,
  Controller,
  FreeMode,
  Navigation,
  Virtual,
  Pagination,
  Mousewheel,
} from "swiper/modules";
import { ref, watch, computed } from "vue";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/free-mode";
import { Swiper, SwiperSlide } from "swiper/vue";
import CustomSwiperControl from "@/components/UI/CustomSwiperComponents/CustomSwiperControl.vue";

import BrandsSkeleton from "@/widgets/home/PopularBrands/BrandsSkeleton.vue";
import DeferredContent from "primevue/deferredcontent";
import YellowAiTip from "@/widgets/common/YellowAiTip.vue";

import { getCapsulesPreview } from "@/features/capsules/api/capsules";
import type { CapsulesPreview } from "@/types/CapsulesTypes";
import type { Audience } from "@/features/recommendations/api/recommendations";
import CustomCapsuleComponent from "@/components/UI/CustomCapsuleComponent/CustomCapsuleComponent.vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import CapsulesSkeletons from "@/widgets/home/Capsules/CapsulesSkeletons.vue";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const DEFAULT_AUDIENCE: Audience = "woman";

const audience = computed<Audience>(() => {
  const value = route.query.audience;
  if (Array.isArray(value)) {
    return (value[0] ?? DEFAULT_AUDIENCE) as Audience;
  }
  return (value ?? DEFAULT_AUDIENCE) as Audience;
});

const swiperModules = [
  FreeMode,
  Autoplay,
  Navigation,
  Controller,
  Virtual,
  Pagination,
  Mousewheel,
];
const swiperBreakPoints = {
  360: {
    slidesPerView: 1.25,
  },
  480: {
    slidesPerView: 1.5,
  },
  768: {
    slidesPerView: 2.5,
  },
  1024: {
    slidesPerView: 3.2,
  },
  1920: {
    slidesPerView: 3.2,
  },
};

const capsules = ref<CapsulesPreview[]>([]);
const isCapsulesLoading = ref(false);

async function loadCapsulesData() {
  isCapsulesLoading.value = true;
  const res = await getCapsulesPreview(10, audience.value);
  capsules.value = res.data;
  isCapsulesLoading.value = false;
}

watch(audience, async () => {
  await loadCapsulesData();
});
</script>

<template>
  <DeferredContent @load="loadCapsulesData">
    <div class="capsules__wrapper">
      <capsules-skeletons v-if="isCapsulesLoading" />
      <div v-else class="capsules-swiper-wrapper">
        <swiper
          :free-mode="true"
          :space-between="20"
          :slidesPerGroup="1"
          :breakpoints="swiperBreakPoints"
          :modules="swiperModules"
          :pagination="{
            clickable: true,
          }"
          :mousewheel="{
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
          }"
          class="capsules-carousel__container"
        >
          <template #container-start>
            <div class="capsules__header">
              <div class="capsules__header-title">
                <span class="capsules__title h1">{{ t("home_page.capsules") }}</span>
                <yellow-ai-tip :ai-tip-text="t('ai_tips.products_easy_combine')" />
              </div>
              <custom-swiper-control
                :control-color="'rgba(35, 47, 69, 1)'"
                class="hide"
              />
            </div>
          </template>
          <swiper-slide v-for="(capsuleItem, index) in capsules" :key="index">
            <custom-capsule-component :capsules-preview-item="capsuleItem" :key="index" />
          </swiper-slide>
        </swiper>
      </div>
    </div>
  </DeferredContent>
</template>

<style scoped>
:deep(.capsules__header) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 220px 20px 0;
}

.capsules__header-title {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.capsules__title {
  color: rgba(35, 47, 69, 1);
}

.capsules__wrapper {
  display: flex;
  padding-left: 5%;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  border-top-right-radius: 40px;
  border-top-left-radius: 40px;
}

.capsules-carousel__container {
  padding: 10px 0;
  user-select: none;
}

.swiper-slide {
  cursor: grab;
}

.swiper-slide:active {
  cursor: grabbing;
}

@media (max-width: 1024px) {
  .capsules__wrapper {
    padding: 0 0 0 16px;
  }

  .hide {
    display: none !important;
  }

  :deep(.capsules__header) {
    padding: 16px;
  }
}
</style>
